import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOrderDraft } from "../../context/OrderDraftContext";
import { useSession } from "../../context/SessionContext";
import { useApi } from "../../hooks/useApi";
import { useKitRecommendation } from "../kits/useKitRecommendation";
import { packagesService } from "../../services/packages";
import { saveService } from "../../services/save";
import { ApiProcedureKit, Kit, ProcedureItem } from "../../types/order.types";
import { SaveResponse } from "../../types/save.types";
import { procedureKitFromApi, procedureToApi } from "../../lib/mappers";
import { proceduresPayload } from "./saveProcedures";
import { PATHS } from "../../app/paths";
import { notify } from "../../components/ui/Toast";
import { t } from "../../i18n";

export const CUSTOM = "Pacotes Personalizados";
export const STANDARD = "Pacotes Padronizados";

// Pacotes: só busca com a aba aberta; a IA só recomenda quando o médico pede (recommend).
export const useProcedureKits = (active: boolean) => {
  const {
    newInputRequest, laudoText, timestamp, initialCheckboxItems, setCheckedKit,
    selectedCheckKitsType, setSelectedCheckKitsType, selectedProcedureKitIndex, setSelectedProcedureKitIndex,
  } = useOrderDraft();
  const { metadata } = useSession();
  const kitsApi = useApi<ApiProcedureKit[]>();
  const saveApi = useApi<SaveResponse>();
  const navigate = useNavigate();
  const [kits, setKits] = useState<Kit<ProcedureItem>[] | null>(null);
  const createdName = useRef<string | null>(null);
  const [recommending, setRecommending] = useState(false);

  const custom = selectedCheckKitsType === CUSTOM;
  const { remember, mark } = useKitRecommendation(`proc:${selectedCheckKitsType}`);
  const { request, apiFetch } = kitsApi;

  // recomendar = o médico pediu a IA: a lista continua na tela enquanto ela escolhe.
  const load = useCallback(async (recomendar = false) => {
    if (!recomendar) setKits(null);
    const body = { historico: newInputRequest, laudo: laudoText, recomendar, ...(custom ? { email: metadata.email } : { especialidade: metadata.especialidade }) };
    const data = await request(() => packagesService.procedureKits(apiFetch, custom ? "personalizados" : "padronizados", body));
    if (recomendar && !data) return;
    const list = (data ?? []).map(procedureKitFromApi);
    setKits(list);
    // Sem reordenar: o pedido salvo guarda a posição do pacote nesta lista.
    // Fica escolhido o pacote recém-criado ou o que a IA acabou de recomendar; senão, o que já estava.
    const created = list.findIndex((k) => k.name === createdName.current);
    createdName.current = null;
    const ai = recomendar ? list.findIndex((k) => k.recommended) : -1;
    if (ai >= 0) remember(list[ai].id);
    else if (recomendar) notify({ tone: "info", title: t("kits.noRecommendation"), body: t("kits.noRecommendationBody") });
    setSelectedProcedureKitIndex((i: number) => (created >= 0 ? created : ai >= 0 ? ai : i));
  }, [remember, request, apiFetch, custom, newInputRequest, laudoText, metadata.email, metadata.especialidade, setSelectedProcedureKitIndex]);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  const recommend = async () => {
    setRecommending(true);
    await load(true);
    setRecommending(false);
  };

  const selectedKit = kits?.[selectedProcedureKitIndex] ?? null;

  const changeType = (type: string) => {
    if (type === selectedCheckKitsType) return;
    setSelectedCheckKitsType(type);
    setSelectedProcedureKitIndex(-1);
  };

  // O pacote novo é personalizado: abre essa aba já atualizada e com ele escolhido.
  const showCreated = (name: string) => {
    createdName.current = name;
    if (custom) load();
    else changeType(CUSTOM);
  };

  const confirm = async () => {
    if (!selectedKit) return;
    const rows = selectedKit.items.map(procedureToApi);
    setCheckedKit(rows);
    const payload = proceduresPayload(timestamp, initialCheckboxItems, rows, {
      tipo: "pacotes",
      tipoDoPacote: selectedCheckKitsType,
      indice: selectedProcedureKitIndex,
    });
    const result = await saveApi.request(() => saveService.post(saveApi.apiFetch, payload));
    if (result?.status === "success") navigate(PATHS.opme);
  };

  return {
    kits: kits && mark(kits),
    recommend,
    recommending,
    type: selectedCheckKitsType,
    custom,
    changeType,
    selected: selectedProcedureKitIndex,
    select: setSelectedProcedureKitIndex,
    selectedKit,
    showCreated,
    confirm,
    saving: saveApi.loading,
  };
};
